import React from "react";

import { find, isEmpty } from "lodash/fp";
import { DateTime, Info } from "luxon";

import {
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import SettingAccordion from "@app/analytics/account/components/SettingAccordion";
import SettingsToggle from "@app/analytics/components/SettingsToggle";
import useAuth from "@app/common/auth/useAuth";
import type {
  UserRssLinkSettings,
  WorkdayDay,
  RssLinkSettingsWorkDay,
  PartialBy,
} from "@app/common/types";
import { replaceWhere } from "@app/utils/arrays";

import Checkbox from "@rui/Checkbox";
import Stack from "@rui/Stack";
import TimePicker from "@rui/TimePicker";
import TimezoneSelector from "@rui/TimezoneSelector";

const DAYS: { value: WorkdayDay; infoIndex: number }[] = [
  { value: "sunday", infoIndex: 6 }, // luxon starts the weekday list at Monday
  { value: "monday", infoIndex: 0 },
  { value: "tuesday", infoIndex: 1 },
  { value: "wednesday", infoIndex: 2 },
  { value: "thursday", infoIndex: 3 },
  { value: "friday", infoIndex: 4 },
  { value: "saturday", infoIndex: 5 },
];

const WEEKDAY_LABELS = Info.weekdays("long");

// use the localized names of weekdays
const DAY_OPTIONS = DAYS.map(({ infoIndex, ...day }) => ({
  ...day,
  label: WEEKDAY_LABELS[infoIndex],
}));

const DEFAULT_WORKING_HOURS = {
  startTime: "09:00",
  endTime: "17:00",
};

const DEFAULT_EMPTY_WORKING_HOURS = {
  startTime: null,
  endTime: null,
};

const DEFAULT_AVAILABILITY: UserRssLinkSettings["workingHours"] = [
  { day: "monday", ...DEFAULT_WORKING_HOURS },
  { day: "tuesday", ...DEFAULT_WORKING_HOURS },
  { day: "wednesday", ...DEFAULT_WORKING_HOURS },
  { day: "thursday", ...DEFAULT_WORKING_HOURS },
  { day: "friday", ...DEFAULT_WORKING_HOURS },
];

const StyledTableRow = styled(TableRow)({
  "&:nth-of-type(even)": {
    backgroundColor: "#FAFAFA",
  },
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.shades.neutral[700],
  padding: theme.spacing(0.25),
  borderBottom: 0,
  "&:first-of-type": {
    width: "180px",
  },
  "&:last-of-type": {
    paddingRight: theme.spacing(1.5),
  },
}));

const Day = ({
  config,
  day,
  onChangeHours,
  onToggleDay,
  label,
}: {
  config: RssLinkSettingsWorkDay;
  day: WorkdayDay;
  onChangeHours: ({
    day,
    startTime,
    endTime,
  }: PartialBy<RssLinkSettingsWorkDay, "startTime" | "endTime">) => void;
  onToggleDay: ({ day, enable }: { day: WorkdayDay; enable: boolean }) => void;
  label: string;
}) => {
  const dayIsChecked = Boolean(config.startTime && config.endTime);

  const handleChangeStartTime = (time: DateTime | null) => {
    if (!time) return;
    const formattedTime = time.toFormat("HH:mm");
    onChangeHours({ day, startTime: formattedTime });
  };

  const handleChangeEndTime = (time: DateTime | null) => {
    if (!time) return;
    const formattedTime = time.toFormat("HH:mm");
    onChangeHours({ day, endTime: formattedTime });
  };

  const parsedStartTime = config.startTime
    ? DateTime.fromFormat(config.startTime, "HH:mm")
    : undefined;
  const parsedEndTime = config.endTime
    ? DateTime.fromFormat(config.endTime, "HH:mm")
    : undefined;

  return (
    <StyledTableRow>
      <StyledTableCell>
        <FormControlLabel
          control={
            <Checkbox
              checked={dayIsChecked}
              onChange={(event, checked) =>
                onToggleDay({ day, enable: checked })
              }
            />
          }
          label={label}
          sx={{ m: 0 }}
        />
      </StyledTableCell>
      {dayIsChecked ? (
        <>
          <StyledTableCell align="center">
            <TimePicker
              value={parsedStartTime}
              maxTime={parsedEndTime}
              onChange={handleChangeStartTime}
            />
          </StyledTableCell>
          <StyledTableCell align="center">to</StyledTableCell>
          <StyledTableCell align="center">
            <TimePicker
              value={parsedEndTime}
              minTime={parsedStartTime}
              onChange={handleChangeEndTime}
            />
          </StyledTableCell>
        </>
      ) : (
        <StyledTableCell
          colSpan={3}
          sx={{ color: "shades.neutral.500", paddingLeft: "30px" }}
        >
          Unavailable
        </StyledTableCell>
      )}
    </StyledTableRow>
  );
};

const AvailableHours = ({
  settings,
  onSettingUpdate,
}: {
  settings: UserRssLinkSettings;
  onSettingUpdate: (update: Partial<UserRssLinkSettings>) => void;
}) => {
  const { impersonating } = useAuth();

  const handleToggleAvailableHoursEnabled = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const currentWorkingHours = settings.workingHours;
    onSettingUpdate({
      // set the timezone to the user's browser timezone unless already set, or
      // if the current user is impersonating another user
      timezone:
        settings.timezone || impersonating
          ? undefined
          : DateTime.local().zone.name,
      workingHoursEnabled: checked,
      // initialize the working hours as the default if they're enabling working
      // hours for the first time
      workingHours:
        isEmpty(currentWorkingHours) && checked
          ? DEFAULT_AVAILABILITY
          : undefined,
    });
  };

  const handleToggleWorkDay = ({
    day,
    enable,
  }: {
    day: WorkdayDay;
    enable: boolean;
  }) => {
    const currentWorkingHours = settings.workingHours;
    const updatedWorkdayConfig = {
      day,
      ...(enable ? DEFAULT_WORKING_HOURS : DEFAULT_EMPTY_WORKING_HOURS),
    };

    const updatedWorkingHours = replaceWhere(
      currentWorkingHours,
      updatedWorkdayConfig,
      (workDay) => workDay.day === day
    );

    onSettingUpdate({
      workingHours: updatedWorkingHours,
    });
  };

  const handleChangeWorkdayHours = ({
    day,
    startTime,
    endTime,
  }: PartialBy<RssLinkSettingsWorkDay, "startTime" | "endTime">) => {
    const currentWorkingHours = settings.workingHours;
    const currentWorkdayHours = find({ day }, currentWorkingHours);
    const updatedHours = {
      day,
      startTime: startTime ?? currentWorkdayHours?.startTime ?? null,
      endTime: endTime ?? currentWorkdayHours?.endTime ?? null,
    };
    const updatedWorkingHours = replaceWhere(
      currentWorkingHours,
      updatedHours,
      (config) => config.day === day
    );
    onSettingUpdate({ workingHours: updatedWorkingHours });
  };

  const handleChangeTimezone = (timezone: string) => {
    onSettingUpdate({ timezone });
  };

  return (
    <Stack sx={{ px: 2 }} spacing={1.5}>
      <SettingAccordion
        title="Available hours"
        description="When enabled, Smart Scheduler will enforce scheduling only during the available hours that you set."
        defaultExpanded={settings.workingHoursEnabled}
        expandDisabled={!settings.workingHoursEnabled}
        Toggle={
          <SettingsToggle
            checked={settings.workingHoursEnabled}
            onChange={handleToggleAvailableHoursEnabled}
          />
        }
        detailsSx={{ px: 0, pb: 0 }}
      >
        <Stack spacing={2} sx={{ pt: 2 }}>
          <TimezoneSelector
            onChange={handleChangeTimezone}
            selected={settings.timezone}
            disableDefaultTimezone
          />
          <Table>
            <TableBody>
              {DAY_OPTIONS.map((day) => (
                <Day
                  key={day.value}
                  label={day.label}
                  day={day.value}
                  onToggleDay={handleToggleWorkDay}
                  onChangeHours={handleChangeWorkdayHours}
                  config={
                    find({ day: day.value }, settings.workingHours) ?? {
                      day: day.value,
                      ...DEFAULT_EMPTY_WORKING_HOURS,
                    }
                  }
                />
              ))}
            </TableBody>
          </Table>
        </Stack>
      </SettingAccordion>
    </Stack>
  );
};

export default AvailableHours;
